import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit} from "@angular/core";
import {Settings} from "~/app/core/model/settings";
import {OfferService} from "~/app/core/offer-service/offer.service";
import {UserProfileService} from "~/app/core/user-profile-service/user-profile.service";
import * as Log from "~/app/core/log";
import {BasePage} from "~/app/pages/base.page";
import {Subscription} from "rxjs";
import * as moment from "moment";
import {InteractionType} from "~/app/core/model/interaction-type";
import {InteractionComponent} from "~/app/core/model/interaction-component";
import {UserProperty} from "~/app/core/model/user-property";
import {InteractionTimer} from "~/app/core/interaction.service";
import {CoreService} from "~/app/core/core.service";
import {TimerObservable} from "rxjs-compat/observable/TimerObservable";

@Component({
    selector: "Settings",
    templateUrl: "./settings.page.html",
    styleUrls: ["./settings.page.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SettingsPage extends BasePage implements OnInit, OnDestroy {
    welcomed: boolean = false;
    loggedIn: boolean = false;
    userEmail: string;
    alreadyRedeemed: boolean = false;

    allowNotifications: boolean = false;
    useLocation: boolean = false;
    useCamera: boolean = false;
    usePhotos: boolean = false;

    localSettings: Settings;
    originalSettings: Settings;

    showToggleSettingsButton: "none" | "notifications" | "location" | "camera" | "storage" = "none";

    get tickets(): number {
        return this.core.api.tickets_getAvailableTicketCount();
    }

    get calculatedTime(): Date {
        return this.localSettings ? this.localSettings.calculateCurrentTime() : new Date();
    }

    private wordTapStage: number = 0;
    private wordTapExpire: number = 0;
    private wordTapSequence: string = "";
    private userId: number;
    private timer: Subscription;
    private pageTimer: InteractionTimer;

    constructor(
        private readonly cd: ChangeDetectorRef,
        private readonly core: CoreService,
        private readonly offerService: OfferService,
        private readonly userProfileService: UserProfileService,
    ) {
        super();
        this.originalSettings = this.settings;
        this.core.api.interactOther({ pgSettingsCount: 1 });
        this.pageTimer = this.core.api.interactTimerOther("pgSettingsTime");
        // Use the component constructor to inject providers.
        const userInfo = this.core.api.user_info();
        this.userId = userInfo ? userInfo.id : null;

        const timer = TimerObservable.create(1000, 60000);
        this.timer = timer.subscribe((t) => {
            this.updateView();
        });
    }

    ngOnInit(): void {
        this.reload();
    }

    ionViewWillEnter() {
        this.reload();
    }

    private reload() {
        this.readStatus();

        // Init your component properties here.
        this.updateNotificationSwitches();
        this.updateLocationSwitch();
        this.updateCameraSwitch();
        this.updatePhotosSwitch();

        this.core.api.device_refreshRemoteSettings().then(() => {
            this.readStatus();
        });
    }

    ngOnDestroy() {
        Log.debug(Log.Tag.SettingsPage, "ngdestroy");
        if (this.timer != null) {
            this.timer.unsubscribe();
            this.timer = null;
        }
        this.pageTimer.log();
    }

    onLaunchSettingsApp() {
        this.showToggleSettingsButton = "none";
        this.core.nav.launchSettingsApp();
    }

    tapLogout() {
        this.core.dialogs.confirm({
            title: "Logout",
            message: "You are about log out of WhutsFree. Are you sure?",
            okButtonText: "Log Out",
            cancelButtonText: "Cancel"
        }).then((result) => {
            if (result === true) {
                this.core.api.interaction(InteractionType.Logout, InteractionComponent.SettingsPage);
                this.core.nav.navPageLogout();
            }
        });
    }

    tapLogin() {
        this.core.nav.navPageLogin(null, true);
    }

    onChangeUseBouncebackNotification(event: boolean): void {
        if (this.localSettings.useBouncebackNotifications !== event) {
            if (this.localSettings.useBouncebackNotifications === true) {
                this.localSettings.useBouncebackNotifications = false;
                this.core.api.device_saveSettings(this.localSettings);
                // this.core.notify.updateNotificationPermissions();
            } else {
                this.localSettings.useBouncebackNotifications = false;
                this.core.notify.requestNotificationPermission().then((allow) => {
                    if (!allow) {
                        this.core.toast.toastError({
                            header: "Notifications Unavailable",
                            message: "Unable to enable notifications. Visit your device Settings to enable the Notification permission for WhutsFree."
                        });
                        this.showToggleSettingsButton = "notifications";
                        this.cd.detectChanges();
                    } else {
                        this.localSettings.useBouncebackNotifications = true;
                        this.core.api.device_saveSettings(this.localSettings);
                        // this.core.notify.updateNotificationPermissions();
                        this.updateNotificationSwitches();
                    }
                }, (error) => {
                    this.core.toast.toastError({
                        header: "Notifications Unavailable",
                        message: "Unable to enable notifications. Visit your device Settings to enable the Notification permission for WhutsFree."
                    });
                });
            }
        }
    }

    onChangeUseReminderNotification(event: boolean): void {
        if (this.localSettings.useReminderNotifications !== event) {
            if (this.localSettings.useReminderNotifications === true) {
                this.localSettings.useReminderNotifications = false;
                this.core.api.device_saveSettings(this.localSettings);
                // this.core.notify.updateNotificationPermissions();
            } else {
                this.localSettings.useReminderNotifications = false;
                this.core.notify.requestNotificationPermission().then((allow) => {
                    if (!allow) {
                        this.core.toast.toastError({
                            header: "Notifications Unavailable",
                            message: "Unable to enable notifications. Visit your device Settings to enable the Notification permission for WhutsFree."
                        });
                        this.showToggleSettingsButton = "notifications";
                        this.cd.detectChanges();
                    } else {
                        this.localSettings.useReminderNotifications = true;
                        this.core.api.device_saveSettings(this.localSettings);
                        this.updateNotificationSwitches();
                        // this.core.notify.updateNotificationPermissions();
                    }
                }, (error) => {
                    this.core.toast.toastError({
                        header: "Notifications Unavailable",
                        message: "Unable to enable notifications. Visit your device Settings to enable the Notification permission for WhutsFree."
                    });
                });
            }
        }
    }

    onChangeUseNewOfferNotification(event: boolean): void {
        if (this.localSettings.useNewOfferNotifications !== event) {
            if (this.localSettings.useNewOfferNotifications === true) {
                this.localSettings.useNewOfferNotifications = false;
                this.core.api.device_saveSettings(this.localSettings);
                // this.core.notify.updateNotificationPermissions();
                // this.core.notify.subscribeToNotificationTopics();
            } else {
                this.localSettings.useNewOfferNotifications = false;
                this.core.notify.requestNotificationPermission().then((allow) => {
                    if (!allow) {
                        this.core.toast.toastError({
                            header: "Notifications Unavailable",
                            message: "Unable to enable notifications. Visit your device Settings to enable the Notification permission for WhutsFree."
                        });
                        this.showToggleSettingsButton = "notifications";
                        this.cd.detectChanges();
                    } else {
                        this.localSettings.useNewOfferNotifications = true;
                        this.core.api.device_saveSettings(this.localSettings);
                        this.updateNotificationSwitches();
                        // this.core.notify.updateNotificationPermissions();
                        // this.core.notify.subscribeToNotificationTopics();
                    }
                }, (error) => {
                    this.core.toast.toastError({
                        header: "Notifications Unavailable",
                        message: "Unable to enable notifications. Visit your device Settings to enable the Notification permission for WhutsFree."
                    });
                });
            }
        }
    }

    onChangeUseLocation(event: boolean): void {
        if (this.useLocation !== event) {
            if (this.useLocation === true) {
                this.useLocation = true;
                this.alreadyHavePermissionDialog("Location");
            } else {
                this.useLocation = false;
                this.core.location.requestLocationPermission().then((result) => {
                    if (!result) {
                        this.core.toast.toastError({
                            header: "Location Unavailable",
                            message: "We're unable to access your device's location.\n\nYou'll need to enable the Location permission from the Settings app."
                        });
                        this.showToggleSettingsButton = "location";
                        this.cd.detectChanges();
                    }
                    this.updateLocationSwitch();
                }, (error) => {
                    this.core.toast.toastError({
                        header: "Location Unavailable",
                        message: "We're not sure what went wrong, but we're unable to access your device's location."
                    });
                });
            }
        }
    }

    onChangeUseCamera(event: boolean): void {
        if (this.useCamera !== event) {
            if (this.useCamera === true) {
                this.useCamera = true;
                this.alreadyHavePermissionDialog("Camera");
            } else {
                this.useCamera = false;
                // this.core.camera.requestCameraPermission().then((result) => {
                //     if (!result) {
                //         this.core.toast.toastError({
                //             header: "Camera Unavailable",
                //             message: "We're unable to access your device's camera.\n\nYou'll need to enable the Camera permission from the Settings app."
                //         });
                //         this.showToggleSettingsButton = "camera";
                //         this.cd.detectChanges();
                //     }
                //     this.updateCameraSwitch();
                // }, (error) => {
                //     // nothing
                //     this.core.toast.toastError({
                //         header: "Camera Unavailable",
                //         message: "We're not sure what went wrong, but we're unable to access your device's camera."
                //     });
                // });
            }
        }
    }

    onChangeUseStorage(event: boolean): void {
        if (this.usePhotos !== event) {
            if (this.usePhotos === true) {
                this.usePhotos = true;
                this.alreadyHavePermissionDialog("Storage");
            } else {
                this.usePhotos = false;
                // this.core.camera.requestStoragePermission().then((result) => {
                //     if (!result) {
                //         this.core.toast.toastError({
                //             header: "Storage Unavailable",
                //             message: "We're unable to access your device's photo storage.\n\nYou'll need to enable the Storage permission from the Settings app."
                //         });
                //         this.showToggleSettingsButton = "storage";
                //         this.cd.detectChanges();
                //     }
                //     this.updatePhotosSwitch();
                // }, (error) => {
                //     Log.debug(Log.Tag.SettingsPage, "REJECTED USE PHOTOS");
                //     Log.debug(Log.Tag.SettingsPage, error);
                //     this.core.toast.toastError({
                //         header: "Storage Unavailable",
                //         message: "We're not sure what went wrong, but we're unable to access your device's photo storage."
                //     });
                // });
            }
        }
    }

    onChangeLogBufferSize(event: any): void {
        const z: number = event.value.length > 0 ? Number(event.value) : null;
        if (z !== this.localSettings.logBufferSize) {
            this.localSettings.logBufferSize = z;
            this.core.api.device_saveSettings(this.localSettings);
        }
    }

    onChangeSponsorNextSeconds(event: any): void {
        if (event.value.length > 0 && this.localSettings.sponsorVideoNextSeconds !== Number(event.value)) {
            this.localSettings.sponsorVideoNextSeconds = Number(event.value);
            this.core.api.device_saveSettings(this.localSettings);
        }
    }

    onChangeSponsorVideoSkip(event: any): void {
        if (event.value.length > 0 && this.localSettings.sponsorVideoSkipDelaySeconds !== Number(event.value)) {
            this.localSettings.sponsorVideoSkipDelaySeconds = Number(event.value);
            this.core.api.device_saveSettings(this.localSettings);
        }
    }

    onChangeDateOverride(event: any): void {
        if (this.localSettings.dateOverride !== Boolean(event.value).valueOf()) {
            this.localSettings.dateOverride = Boolean(event.value).valueOf();
            if (this.localSettings.dateOverride) {
                this.localSettings.dateSimulate = moment().format("YYYY-MM-DD");
            }
            this.core.api.device_saveSettings(this.localSettings);
            this.core.api.offers_clearSearchResults();
            this.updateView();
        }
    }

    onChangeDateSimulate(event: any): void {
        if (this.localSettings.dateSimulate !== event.value) {
            if (moment(event.value, "YYYY-MM-DD", true).isValid()) {
                this.localSettings.dateSimulate = event.value;
                this.core.api.device_saveSettings(this.localSettings);
                this.core.api.offers_clearSearchResults();
            }
            this.updateView();
        }
    }

    onChangeHoursShift(event: any): void {
        if (this.localSettings.hoursShift !== Number(event.value)) {
            const shift = event.value.length > 0 ? Number(event.value) : null;
            if (shift !== null && shift !== Number.NaN && shift > -100000 && shift < 100000) {
                this.localSettings.hoursShift = shift;
                this.core.api.device_saveSettings(this.localSettings);
                this.core.api.offers_clearSearchResults();
            }
            this.updateView();
        }
    }

    onChangeGamificationTicketsEnabled(event: boolean): void {
        if (this.localSettings.gamificationEnabled !== event) {
            this.localSettings.gamificationEnabled = event;
            this.core.api.device_saveSettings(this.localSettings);
        }
    }

    onChangeGamificationDailyEnabled(event: boolean): void {
        if (this.localSettings.gamificationDailyProgressEnabled !== event) {
            this.localSettings.gamificationDailyProgressEnabled = event;
            this.core.api.device_saveSettings(this.localSettings);
        }
    }

    onChangeGamificationMysteryMode(event: boolean): void {
        if (this.localSettings.gamificationMysteryMode !== event) {
            this.localSettings.gamificationMysteryMode = event;
            this.core.api.device_saveSettings(this.localSettings);
        }
    }

    onChangeGamificationFavoriteMode(event: boolean): void {
        if (this.localSettings.gamificationFavoriteMode !== event) {
            this.localSettings.gamificationFavoriteMode = event;
            this.core.api.device_saveSettings(this.localSettings);
        }
    }

    onChangeFavoriteCost(event: boolean): void {
        if (this.localSettings.favoriteCost !== Number().valueOf()) {
            this.localSettings.favoriteCost = Number().valueOf();
            this.core.api.device_saveSettings(this.localSettings);
        }
    }

    onChangeTickets(event: boolean): void {
        if (this.core.api.tickets_getAvailableTicketCount() !== Number().valueOf()) {
            this.core.api.tickets_setTickets(Number().valueOf());
        }
    }

    onChangeSignupBeforeNoOffers(event: boolean): void {
        if (this.localSettings.signupBeforeNoOffers !== event) {
            this.localSettings.signupBeforeNoOffers = event;
            this.core.api.device_saveSettings(this.localSettings);
        }
    }

    onChangeSignupBeforeAnyOffers(event: boolean): void {
        if (this.localSettings.signupBeforeAnyOffers !== event) {
            this.localSettings.signupBeforeAnyOffers = event;
            this.core.api.device_saveSettings(this.localSettings);
        }
    }

    onChangeQuickScroll(event: boolean): void {
        if (this.localSettings.quickScroll !== event) {
            this.localSettings.quickScroll = event;
            this.core.api.device_saveSettings(this.localSettings);
        }
    }

    onChangeQuickShare(event: boolean): void {
        if (this.localSettings.quickShare !== event) {
            this.localSettings.quickShare = event;
            this.core.api.device_saveSettings(this.localSettings);
        }
    }

    onBusinessShowShareCoupon(event: boolean): void {
        if (this.localSettings.showShareCoupon !== event) {
            this.localSettings.showShareCoupon = event;
            this.core.api.device_saveSettings(this.localSettings);
        }
    }

    onChangeShowBusinessCoupon(event: boolean): void {
        if (this.localSettings.showBusinessCoupon !== event) {
            this.localSettings.showBusinessCoupon = event;
            this.core.api.device_saveSettings(this.localSettings);
        }
    }

    onChangeShowMilesAwayCoupon(event: boolean): void {
        if (this.localSettings.showMilesAwayOnCoupon !== event) {
            this.localSettings.showMilesAwayOnCoupon = event;
            this.core.api.device_saveSettings(this.localSettings);
        }
    }

    onChangePollThumbsUpBeforeBounceback(event: boolean): void {
        if (this.localSettings.pollThumbsUpBeforeBounceback !== event) {
            this.localSettings.pollThumbsUpBeforeBounceback = event;
            this.core.api.device_saveSettings(this.localSettings);
        }
    }

    onChangeAlwaysShowBouncebacks(event: boolean): void {
        if (this.localSettings.alwaysShowBouncebacks !== event) {
            this.localSettings.alwaysShowBouncebacks = event;
            this.core.api.device_saveSettings(this.localSettings);
        }
    }

    onChangeAllowFiltering(event: boolean): void {
        if (this.localSettings.allowFiltering !== event) {
            this.localSettings.allowFiltering = event;
            this.core.api.device_saveSettings(this.localSettings);
        }
    }

    onChangeFilteringMinOffers(event: boolean): void {
        if (this.localSettings.filteringMinOffers !== Number().valueOf()) {
            this.localSettings.filteringMinOffers = Number().valueOf();
            this.core.api.device_saveSettings(this.localSettings);
        }
    }

    onChangeRedeemAnywhere(event: boolean): void {
        if (this.localSettings.enforceRedeemLocation !== !event) {
            this.localSettings.enforceRedeemLocation = !event;
            this.core.api.device_saveSettings(this.localSettings);
        }
    }

    onChangeRedeemAllowedMiles(event: boolean): void {
        if (this.localSettings.redeemAllowedMiles !== Number().valueOf()) {
            this.localSettings.redeemAllowedMiles = Number().valueOf();
            this.core.api.device_saveSettings(this.localSettings);
        }
    }

    onChangeRedeemNowMiles(event: boolean): void {
        if (this.localSettings.redeemNowMiles !== Number().valueOf()) {
            this.localSettings.redeemNowMiles = Number().valueOf();
            this.core.api.device_saveSettings(this.localSettings);
        }
    }

    onChangeRedeemWithNet(event: boolean): void {
        if (this.localSettings.enforceRedeemNet !== event) {
            this.localSettings.enforceRedeemNet = event;
            this.core.api.device_saveSettings(this.localSettings);
        }
    }

    onChangeAllowReshowCashier(event: boolean): void {
        if (this.localSettings.allowReshowCashier !== event) {
            this.localSettings.allowReshowCashier = event;
            this.core.api.device_saveSettings(this.localSettings);
        }
    }

    onChangeBouncebackOverride(event: boolean): void {
        if (this.localSettings.bounceBackNotificationOverride !== event) {
            this.localSettings.bounceBackNotificationOverride = event;
            this.core.api.device_saveSettings(this.localSettings);
        }
    }

    onChangeBouncebackDelay(event: any): void {
        if (event.value.length > 0 && this.localSettings.bounceBackNotificationDelaySeconds !== Number(event.value)) {
            this.localSettings.bounceBackNotificationDelaySeconds = Number(event.value);
            this.core.api.device_saveSettings(this.localSettings);
        }
    }

    onChangeSearchExpiresOverride(event: boolean): void {
        if (this.localSettings.searchExpiresOverride !== event) {
            this.localSettings.searchExpiresOverride = event;
            this.core.api.device_saveSettings(this.localSettings);
        }
    }

    onChangeSearchExpires(event: any): void {
        if (event.value.length > 0 && this.localSettings.searchExpiresMinutes !== Number(event.value)) {
            this.localSettings.searchExpiresMinutes = Number(event.value);
            this.core.api.device_saveSettings(this.localSettings);
        }
    }

    onChangeSearchAlertDelaySeconds(event: any): void {
        if (event.value.length > 0 && this.localSettings.searchAlertDelaySeconds !== Number(event.value)) {
            this.localSettings.searchAlertDelaySeconds = Number(event.value);
            this.core.api.device_saveSettings(this.localSettings);
        }
    }

    onChangeReminderNotificationEnabled(event: boolean): void {
        this.localSettings.reminderNotificationEnabled = event;
        this.core.api.device_saveSettings(this.localSettings);
    }

    onChangeReminderOverride(event: boolean): void {
        this.localSettings.reminderNotificationOverride = event;
        this.core.api.device_saveSettings(this.localSettings);
    }

    onChangeReminderNotificationDelay(event: any): void {
        if (event.value.length > 0) {
            this.localSettings.reminderNotificationMinutes = Number(event.value);
            this.core.api.device_saveSettings(this.localSettings);
        }
    }

    onChangeShareOverride(event: boolean): void {
        this.localSettings.shareNotificationOverride = event;
        this.core.api.device_saveSettings(this.localSettings);
    }

    onChangeShareNotificationDelay(event: any): void {
        if (event.value.length > 0) {
            this.localSettings.shareNotificationMinutes = Number(event.value);
            this.core.api.device_saveSettings(this.localSettings);
        }
    }

    onChangeTestingFlag(event: boolean): void {
        if (this.localSettings.testing !== event) {
            this.localSettings.testing = event;
            this.core.api.device_saveSettings(this.localSettings);
        }
    }

    onChangeShowEnvironmentHeader(event: boolean): void {
        this.localSettings.showEnvironmentHeader = event;
        this.core.api.device_saveSettings(this.localSettings);
    }

    onChangeLoadRemoteSettings(event: boolean): void {
        this.localSettings.loadRemoteSettings = event;
        this.core.api.device_saveSettings(this.localSettings);
    }

    onChangeLogging(event: boolean): void {
        this.localSettings.enableLogging = event;
        this.core.api.device_saveSettings(this.localSettings);
    }

    onChangeObscureUnauthenticatedOffer(event: boolean): void {
        this.localSettings.obscureUnauthenticatedOffer = event;
        this.core.api.device_saveSettings(this.localSettings);
    }

    onChangeCombinedOffersTab(event: boolean): void {
        this.localSettings.combinedOffersTab = event;
        this.core.api.device_saveSettings(this.localSettings);
    }

    onChangeInterleavedCoupons(event: boolean): void {
        this.localSettings.interleavedCoupons = event;
        this.core.api.device_saveSettings(this.localSettings);
    }

    onChangeShowShareInstructions(event: boolean): void {
        this.localSettings.showShareInstructions = event;
        this.core.api.device_saveSettings(this.localSettings);
    }

    onChangeShowPermissionSettings(event: boolean): void {
        this.localSettings.showPermissionSettings = event;
        this.core.api.device_saveSettings(this.localSettings);
    }

    onChangeUseSampleOffers(event: boolean): void {
        if (this.localSettings.useSampleOffers !== event) {
            this.offerService.setUseSample(event);
            this.readStatus();
            if (!this.localSettings.useSampleOffers && this.localSettings.useSampleUser) {
                // can't run sample offers with a sample user because of api authentication
                this.userProfileService.setUseSample(false);
            }
        }
    }

    onChangeUseSampleUser(event: boolean): void {
        if (this.localSettings.useSampleUser !== event) {
            this.userProfileService.setUseSample(event);
            this.readStatus();
            if (this.localSettings.useSampleUser && !this.localSettings.useSampleOffers) {
                // can't run sample user without sample offers because of api authentication
                this.offerService.setUseSample(true);
            }
        }
    }

    tapClearTempFiles() {
        this.core.dialogs.confirm({
            title: "Clear Temp Files",
            message: "Delete cached/temporary files (e.g. images) so that they are downloaded fresh.",
            okButtonText: "OK",
            cancelButtonText: "Cancel"
        }).then((result) => {
            if (result === true) {
                this.core.api.device_clearData();
                this.core.toast.toastInfo({
                    header: "Cache Clear",
                    message: "We've begun deleting all temporary files used by the app."
                });
            }
        });
    }

    tapResetDailyTasks() {
        this.core.dialogs.confirm({
            title: "Reset Daily Tasks",
            message: "Reset the daily task tracking so tickets can be re-earned?",
            okButtonText: "OK",
            cancelButtonText: "Cancel"
        }).then((result) => {
            if (result === true) {
                this.core.api.tickets_resetDailyTasks();
                this.core.toast.toastInfo({
                    header: "Daily Tasks Reset",
                    message: "We've reset the daily ticket tasks."
                });
            }
        });
    }

    tapReshowWelcome() {
        this.core.dialogs.confirm({
            title: "Re-show Welcome",
            message: "Reset welcome sequence and display welcome screen right now.",
            okButtonText: "Re-show",
            cancelButtonText: "Cancel"
        }).then((result) => {
            if (result === true) {
                this.core.api.test_resetWelcome();
                this.readStatus();
                this.core.nav.navPageWelcome();
            }
        });
    }

    tapReshowFirstTimeInstructions() {
        this.core.dialogs.confirm({
            title: "Reset First-Time Instructions",
            message: "App reset to display first time redemption dialog on next redeem.",
            okButtonText: "OK",
            cancelButtonText: "Cancel"
        }).then((result) => {
            if (result === true) {
                this.core.api.test_resetFirstRedeem();
                this.readStatus();
            }
        });
    }

    tapResetOffers() {
        this.core.dialogs.confirm({
            title: "Reset Offers",
            message: "You are about to reset the redeemed status of all offers. Are you sure?",
            okButtonText: "Reset",
            cancelButtonText: "Cancel"
        }).then((result) => {
            if (result === true) {
                this.waiting = true;
                this.cd.detectChanges();
                this.core.api.test_resetOffers().then((successResponse) => {
                    this.core.nav.navPageOffers(null, false, "fade", this.core.api);
                }, (error) => {
                    this.waiting = false;
                    this.readStatus();
                    this.core.toast.toastError({
                        header: "Reset Failed",
                        message: "There was a problem resetting the offers."
                    });
                    this.cd.detectChanges();
                });
            }
        });
    }

    tapRestart() {
        this.core.dialogs.confirm({
            title: "Fresh Restart",
            message: "You are about to clear all of your local data and restart the welcome sequence. Are you sure?",
            okButtonText: "Restart",
            cancelButtonText: "Cancel"
        }).then((result) => {
            if (result === true) {
                this.waiting = true;
                this.cd.detectChanges();
                this.core.api.test_resetFresh();
                this.core.nav.navPageSearching();
            }
        });
    }

    tapDeleteUser() {
        this.core.dialogs.confirm({
            title: "Delete User",
            message: "You are about to delete this test user and complete history. If you proceed, you will need to re-register the user. Are you sure?",
            okButtonText: "Delete",
            cancelButtonText: "Cancel"
        }).then((result) => {
            if (result === true) {
                this.waiting = true;
                this.cd.detectChanges();
                this.core.api.test_deleteUser().then((deleteResponse) => {
                    this.waiting = false;
                    this.core.nav.navPageSettings();
                    this.cd.detectChanges();
                }, (error) => {
                    this.waiting = false;
                    this.readStatus();
                    this.core.toast.toastError({
                        header: "Delete Failed",
                        message: "There was a problem deleting the test user."
                    });
                    this.cd.detectChanges();
                });
            }
        });
    }

    tapShowUpgradePage() {
        this.core.nav.navPageUpgrade();
    }

    tapShowReferrerPage() {
        this.core.nav.navPageReferrer(null, true);
    }

    tapShowValidatePage() {
        this.core.nav.navPageValidate(null, true);
    }

    showSetLogLevel() {
        this.core.dialogs.choose({
            message: "Select the log level",
            buttons: ["error", "warn", "info", "debug", "trace"]
        }).then((result) => {
            if (result === "error" || result === "warn" || result === "info" || result === "debug" || result === "trace") {
                this.localSettings.logLevel = result;
                this.core.api.device_saveSettings(this.localSettings);
                this.updateView();
            }
        });
    }

    showSetEnv() {
        this.core.dialogs.choose({
            message: "Select the environment",
            buttons: ["prod", "test", "local"]
        }).then((result) => {
            console.log(result);
            if ((result === "prod" || result === "test" || result === "local") && result !== this.localSettings.env) {
                this.core.api.user_setProperty(UserProperty.Environment, result);
                if (result === "prod") {
                    this.core.api.interaction(InteractionType.EnvProd, InteractionComponent.SettingsPage);
                } else if (result === "test") {
                    this.core.api.interaction(InteractionType.EnvTest, InteractionComponent.SettingsPage);
                } else if (result === "local") {
                    this.core.api.interaction(InteractionType.EnvLocal, InteractionComponent.SettingsPage);
                }
                this.localSettings.env = result;
                this.core.api.device_saveSettings(this.localSettings);
                this.updateView();
            }
        });
    }

    onChangeLocalUrl(event: string): void {
        this.localSettings.localUrl = event;
        this.core.api.device_saveSettings(this.localSettings);
        this.updateView();
    }

    // wordTap(val: number, event: gestures.TouchGestureEventData): void {
        // if (event.action === "down") {
        //     const now: number = new Date().getTime();
        //     if (this.wordTapExpire < now) {
        //         this.wordTapSequence = "";
        //     }
        //     this.wordTapSequence = this.wordTapSequence + String(val);
        //     this.wordTapExpire = now + 1000;
        //
        //     this.validateWordTapSequence();
        //     this.updateView();
        // }
    // }

    onChangeSwipeSensitivity(event: any): void {
        if (this.localSettings.swipeSensitivity !== Number(event.value)) {
            const sensitivity = event.value.length > 0 ? Number(event.value) : null;
            if (sensitivity !== null && sensitivity !== Number.NaN && sensitivity >= 1 && sensitivity < 1000) {
                this.localSettings.swipeSensitivity = sensitivity;
                this.core.api.device_saveSettings(this.localSettings);
                this.core.api.offers_clearSearchResults();
            }
            this.updateView();
        }
    }

    private validateWordTapSequence() {
        if (this.wordTapStage === 0) {
            if (this.wordTapSequence.endsWith("0101010101")) {
                this.core.toast.toastInfo({
                    header: "More info about notifications",
                    message: "Here's some additional detail.",
                    duration: 2000,
                    onHide: () => {
                        this.core.nav.navPageNotificationsStatus(true);
                    }
                });
                this.wordTapSequence = "";
            }
        }
    }

    private alreadyHavePermissionDialog(permissionName: string) {
        this.core.toast.toastInfo({
            header: "Manage Permissions",
            message: "You have already granted the " + permissionName + " permission to WhutsFree. To remove the permission, use the Settings app on your device."
        });
    }

    private updateNotificationSwitches(): void {
        this.core.notify.isAllowNotification().then((allow) => {
            this.readStatus();
            this.allowNotifications = allow;
            const useBouncebackNotifications = allow && this.localSettings.useBouncebackNotifications;
            if (useBouncebackNotifications !== this.localSettings.useBouncebackNotifications) {
                this.localSettings.useBouncebackNotifications = useBouncebackNotifications;
                this.core.api.device_saveSettings(this.localSettings);
            }
            const useReminderNotifications = allow && this.localSettings.useReminderNotifications;
            if (useReminderNotifications !== this.localSettings.useReminderNotifications) {
                this.localSettings.useReminderNotifications = useReminderNotifications;
                this.core.api.device_saveSettings(this.localSettings);
            }
            const useNewOfferNotifications = allow && this.localSettings.useNewOfferNotifications;
            if (useNewOfferNotifications !== this.localSettings.useNewOfferNotifications) {
                this.localSettings.useNewOfferNotifications = useNewOfferNotifications;
                this.core.api.device_saveSettings(this.localSettings);
            }
            this.cd.detectChanges();
        });
    }

    private updateLocationSwitch(): void {
        this.core.location.isAllowLocation().then((allow) => {
            this.useLocation = allow;
            if (!this.useLocation) {
                const search = this.core.api.offers_getSearch();
                this.core.api.offers_setSearch({
                    location: false,
                    zipCode: search.zipCode,
                    radius: search.radius
                });
            }
            this.cd.detectChanges();
        });
    }

    private updateCameraSwitch(): void {
        // this.core.camera.isAllowCamera().then((allow) => {
        //     this.useCamera = allow;
        //     this.cd.detectChanges();
        // });
    }

    private updatePhotosSwitch(): void {
        // this.core.camera.isAllowStorage().then((allow) => {
        //     this.usePhotos = allow;
        //     this.cd.detectChanges();
        // });
    }

    private readStatus() {
        this.localSettings = this.core.api.device_loadSettings();
        this.welcomed = this.core.api.user_isWelcomed();
        this.loggedIn = this.core.api.user_isAuthenticated();
        this.userEmail = this.loggedIn ? this.core.api.user_info().email : "";
        this.alreadyRedeemed = this.core.api.user_alreadyRedeemed();
        if (Log.isTrace(Log.Tag.SettingsPage)) {
            Log.trace(Log.Tag.SettingsPage, "sample offers: " + this.localSettings.useSampleOffers);
            Log.trace(Log.Tag.SettingsPage, "sample user: " + this.localSettings.useSampleUser);
        }
    }

    private updateView() {
        // just wakeup to update timestamp
        this.cd.detectChanges();
    }
}
